import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixSocialGroupsApiV2GroupRequestsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    'editor-flow.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    'groups.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/social-groups-proxy/group-requests',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/group-requests-proxy',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_groups_group-requests';

/**
 * Approves group requests.
 *
 * Only Wix users can approve group requests.
 */
export function approveGroupRequests(
  payload: object
): RequestOptionsFactory<any> {
  function __approveGroupRequests({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group_request',
      method: 'POST' as any,
      methodFqn:
        'wix.social.groups.api.v2.GroupRequestsService.ApproveGroupRequests',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupRequestsServiceUrl({
        protoPath: '/v2/group-requests/approve',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'groupRequest.group.createdDate' },
              { path: 'groupRequest.group.updatedDate' },
              { path: 'groupRequest.group.recentActivityDate' },
              { path: 'groupRequest.group.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'groupRequest.group.details.logoPosition.x' },
              { path: 'groupRequest.group.details.logoPosition.y' },
              { path: 'groupRequest.group.details.mobileLogoPosition.x' },
              { path: 'groupRequest.group.details.mobileLogoPosition.y' },
              { path: 'groupRequest.group.coverImage.position.x' },
              { path: 'groupRequest.group.coverImage.position.y' },
              { path: 'groupRequest.group.coverImage.mobilePosition.x' },
              { path: 'groupRequest.group.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __approveGroupRequests;
}

/**
 * Rejects group requests.
 *
 * Only Wix users can reject group requests.
 */
export function rejectGroupRequests(
  payload: object
): RequestOptionsFactory<any> {
  function __rejectGroupRequests({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group_request',
      method: 'POST' as any,
      methodFqn:
        'wix.social.groups.api.v2.GroupRequestsService.RejectGroupRequests',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupRequestsServiceUrl({
        protoPath: '/v2/group-requests/reject',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'groupRequest.group.createdDate' },
              { path: 'groupRequest.group.updatedDate' },
              { path: 'groupRequest.group.recentActivityDate' },
              { path: 'groupRequest.group.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'groupRequest.group.details.logoPosition.x' },
              { path: 'groupRequest.group.details.logoPosition.y' },
              { path: 'groupRequest.group.details.mobileLogoPosition.x' },
              { path: 'groupRequest.group.details.mobileLogoPosition.y' },
              { path: 'groupRequest.group.coverImage.position.x' },
              { path: 'groupRequest.group.coverImage.position.y' },
              { path: 'groupRequest.group.coverImage.mobilePosition.x' },
              { path: 'groupRequest.group.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __rejectGroupRequests;
}

/**
 * Lists requests to create a group.
 *
 * The `listGroupRequests()` function returns a Promise that resolves to a list of up to 1,000 create group requests on your site.
 *
 * Using the options parameter, you can filter your list of posts, set the amount of posts to be returned, and sort your list in a specified order.
 *
 * By default, the list is sorted by `_createdDate` in descending order. Only admins can see create group requests. Members can access their own create group requests in their site.
 *
 * >**Note:** This function is only relevant if admin approval is required for creating a group.
 *
 */
export function listGroupRequests(payload: object): RequestOptionsFactory<any> {
  function __listGroupRequests({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group_request',
      method: 'GET' as any,
      methodFqn:
        'wix.social.groups.api.v2.GroupRequestsService.ListGroupRequests',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupRequestsServiceUrl({
        protoPath: '/v2/group-requests',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'groupRequests.group.createdDate' },
              { path: 'groupRequests.group.updatedDate' },
              { path: 'groupRequests.group.recentActivityDate' },
              { path: 'groupRequests.group.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'groupRequests.group.details.logoPosition.x' },
              { path: 'groupRequests.group.details.logoPosition.y' },
              { path: 'groupRequests.group.details.mobileLogoPosition.x' },
              { path: 'groupRequests.group.details.mobileLogoPosition.y' },
              { path: 'groupRequests.group.coverImage.position.x' },
              { path: 'groupRequests.group.coverImage.position.y' },
              { path: 'groupRequests.group.coverImage.mobilePosition.x' },
              { path: 'groupRequests.group.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listGroupRequests;
}

/**
 * Creates a query to retrieve a list of create requests.
 *
 * The `queryGroupRequests()` function builds a query to retrieve a list of all requests to create a group, and returns a `GroupRequestsQueryBuilder` object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](/group-requests-query-builder/find) function. You can refine the query by chaining `GroupRequestQueryBuilder` functions onto the query. `GroupRequestQueryBuilder` functions enable you to sort, filter, and control the results that `queryGroupRequests()` returns.
 *
 * `queryGroupRequests()` runs with these `GroupRequestsQueryBuilder` defaults, which you can override:
 * - [`limit(1000)`](/group-requests-query-builder/limit)
 *
 * The following `GroupRequestsQueryBuilder` functions are supported for `queryGroupRequests()`. For a full description of the `GroupRequests` object, see the object returned for the [`items`](/group-requests-query-result/items) property in `GroupRequestsQueryResult`.
 */
export function queryGroupRequests(
  payload: object
): RequestOptionsFactory<any> {
  function __queryGroupRequests({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group_request',
      method: 'POST' as any,
      methodFqn:
        'wix.social.groups.api.v2.GroupRequestsService.QueryGroupRequests',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupRequestsServiceUrl({
        protoPath: '/v2/group-requests/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'groupRequests.group.createdDate' },
              { path: 'groupRequests.group.updatedDate' },
              { path: 'groupRequests.group.recentActivityDate' },
              { path: 'groupRequests.group.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'groupRequests.group.details.logoPosition.x' },
              { path: 'groupRequests.group.details.logoPosition.y' },
              { path: 'groupRequests.group.details.mobileLogoPosition.x' },
              { path: 'groupRequests.group.details.mobileLogoPosition.y' },
              { path: 'groupRequests.group.coverImage.position.x' },
              { path: 'groupRequests.group.coverImage.position.y' },
              { path: 'groupRequests.group.coverImage.mobilePosition.x' },
              { path: 'groupRequests.group.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryGroupRequests;
}
